import type { ChatMessage } from '../types/chat'

const CHAT_PREFIX = 'chat-cache-'

export function todayPrefix(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${CHAT_PREFIX}${yyyy}${mm}${dd}`
}

function todayId(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}${mm}${dd}`
}

const LAST_CLEAN_KEY = 'chat-cache-last-clean-date'

export function hasCleanedToday(): boolean {
  try {
    const v = localStorage.getItem(LAST_CLEAN_KEY) || ''
    return v === todayId()
  } catch {
    return false
  }
}

export function markTodayCleaned(): void {
  try {
    localStorage.setItem(LAST_CLEAN_KEY, todayId())
  } catch {}
}

// Deprecated: per-message cache keys are no longer used

export function clearOldDays(): void {
  try {
    const keepPrefix = todayPrefix()
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) || ''
      if (k.startsWith(CHAT_PREFIX) && !k.startsWith(keepPrefix)) keys.push(k)
    }
    keys.forEach(k => {
      try { localStorage.removeItem(k) } catch {}
    })
  } catch {}
}

export function loadTodayMessages(): ChatMessage[] | null {
  try {
    const raw = localStorage.getItem(todayPrefix())
    if (!raw) return null
    const arr = JSON.parse(raw) as Array<Omit<ChatMessage, 'timestamp'> & { timestamp: string }>
    return arr.map(m => ({ ...m, timestamp: new Date(m.timestamp) }))
  } catch {
    return null
  }
}

export function saveTodayMessages(messages: ChatMessage[]): void {
  try {
    const serial = messages.map(m => ({ ...m, timestamp: m.timestamp.toISOString() }))
    localStorage.setItem(todayPrefix(), JSON.stringify(serial))
  } catch {}
}

export function saveMessageAudio(messageId: string, audio: NonNullable<ChatMessage['audio']>): void {
  try {
    const arr = loadTodayMessages() || []
    const idx = arr.findIndex(m => m.id === messageId)
    if (idx !== -1) {
      const updated = { ...arr[idx], audio }
      arr[idx] = updated
      saveTodayMessages(arr)
      return
    }
    const fallback: ChatMessage = { id: messageId, content: '', sender: 'ai', timestamp: new Date(), audio }
    arr.push(fallback)
    saveTodayMessages(arr)
  } catch {}
}

export function removePerMessageKeysForToday(): void {
  try {
    const prefix = `${todayPrefix()}-`
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) || ''
      if (k.startsWith(prefix)) keys.push(k)
    }
    keys.forEach(k => {
      try { localStorage.removeItem(k) } catch {}
    })
  } catch {}
}

export function getMessageFromToday(messageId: string): ChatMessage | null {
  try {
    const arr = loadTodayMessages() || []
    return arr.find(m => m.id === messageId) || null
  } catch {
    return null
  }
}

export function upsertMessage(message: ChatMessage): void {
  try {
    const arr = loadTodayMessages() || []
    const idx = arr.findIndex(m => m.id === message.id)
    if (idx !== -1) {
      arr[idx] = message
    } else {
      arr.push(message)
    }
    saveTodayMessages(arr)
  } catch {}
}

export function scheduleDailyChatCacheCleanup(): number {
  try {
    const runOnce = () => {
      try {
        if (!hasCleanedToday()) {
          clearOldDays()
          markTodayCleaned()
        }
        removePerMessageKeysForToday()
      } catch {}
    }
    runOnce()
    const id = window.setInterval(runOnce, 60 * 60 * 1000)
    return id
  } catch {
    return 0
  }
}
